import { useEffect, useRef } from 'react';

/**
 * History layers: make in-page layers (sheets, the PIN step, wizard steps) behave like
 * pages for the browser / Android Back button.
 *
 *  - While a layer is active it owns one history entry on top of the current page
 *    (same URL, marked in history.state).
 *  - Back pops the entry and calls the layer's onBack (close the sheet, previous step).
 *  - Closing layers from the UI (✕, Save, scrim, a Back button) removes their entries
 *    again — all together, so the next Back is never a dead press.
 *  - If the app navigated to another page meanwhile, leftover entries are simply
 *    forgotten (going back would land somewhere unexpected).
 *
 * Safe under React StrictMode (mount → unmount → mount in development).
 */

interface Layer {
  id: number;
  closed: boolean;
  onBack: () => void;
}

let seq = 0;
const stack: Layer[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let listening = false;

const topMarker = (): number | undefined =>
  (window.history.state as { clearLayer?: number } | null)?.clearLayer;

function onPopState() {
  const top = topMarker() ?? 0;
  // Every layer above the entry we landed on has been popped by Back.
  while (stack.length && stack[stack.length - 1]!.id > top) {
    const l = stack.pop()!;
    if (!l.closed) l.onBack();
  }
  if (stack.length && stack[stack.length - 1]!.closed) scheduleFlush();
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    let n = 0;
    for (let i = stack.length - 1; i >= 0 && stack[i]!.closed; i--) n++;
    if (!n) return;
    const top = stack[stack.length - 1]!;
    stack.splice(stack.length - n, n);
    // Only step back if the browser is still on our topmost entry (no page change since).
    if (topMarker() === top.id) window.history.go(-n);
  }, 0);
}

export function useHistoryLayer(active: boolean, onBack: () => void): void {
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  const layerRef = useRef<Layer | null>(null);

  useEffect(() => {
    if (!active || typeof window === 'undefined') return;
    if (!listening) {
      window.addEventListener('popstate', onPopState);
      listening = true;
    }
    const existing = layerRef.current;
    if (existing && existing.closed && stack.includes(existing)) {
      existing.closed = false; // StrictMode remount: keep the entry pushed a moment ago
    } else {
      const layer: Layer = { id: ++seq, closed: false, onBack: () => onBackRef.current() };
      layerRef.current = layer;
      stack.push(layer);
      window.history.pushState({ ...(window.history.state ?? {}), clearLayer: layer.id }, '');
    }
    const mine = layerRef.current!;
    return () => {
      if (!stack.includes(mine)) return; // already popped by Back
      mine.closed = true;
      scheduleFlush();
    };
  }, [active]);
}
